import { AssetTypeId, SystemTypeId } from '@Types/assetSchema.types';
import { ChoiceListPairs } from '@Utils/choiceListFilesUtils';
import { RECORD_TYPE } from '@Utils/choiceListMapping';
import {
	getMigratedRecordsFromFlag,
	getRecordTypeOptions,
} from '@Utils/recordTypeOptionsUtils';
import {
	assetSchemaSelectorByType,
	getAssetSchemaAction,
} from '@innovyze/lib_asset_schema_store';
import { useGlobalization, useIsFeatureEnabled } from '@innovyze/stylovyze';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface UseRecordTypeOptionsArgs {
	systemType: SystemTypeId;
	assetType: AssetTypeId;
}

export function useRecordTypeOptions({
	systemType,
	assetType,
}: UseRecordTypeOptionsArgs): {
	enabledRecordTypes: RECORD_TYPE[];
	recordTypeOptionsFlagged: ChoiceListPairs[];
} {
	const { t } = useGlobalization();

	const dispatch = useDispatch();

	const rehabRulesFlag = useIsFeatureEnabled(
		'info-360-rehab-stormwater-rules',
	) as unknown as string;

	const migratedRecords = getMigratedRecordsFromFlag(rehabRulesFlag);

	useEffect(() => {
		dispatch(
			getAssetSchemaAction({
				systemType: systemType.id,
				assetType: assetType.id,
			}),
		);
	}, [dispatch, systemType, assetType]);

	const schema = useSelector(state =>
		assetSchemaSelectorByType(state, assetType.id),
	);

	const recordTypeOptionsPairDefinitions: ChoiceListPairs[] = [
		{ label: t('Asset attributes'), value: RECORD_TYPE.ASSET_ATTRIBUTES },
		{ label: t('Custom Table'), value: RECORD_TYPE.USER_DEFINED_FIELDS },
		{ label: t('Task'), value: RECORD_TYPE.TASK },
		{ label: t('Event'), value: RECORD_TYPE.EVENT },
		{ label: t('Action'), value: RECORD_TYPE.ACTION },
		{ label: t('Condition'), value: RECORD_TYPE.CONDITION },
		{ label: t('Inspection'), value: RECORD_TYPE.INSPECTION },
		{ label: t('Risk'), value: RECORD_TYPE.RISK },
	];

	const recordTypeOptions = schema
		? getRecordTypeOptions(schema, migratedRecords)
		: [];

	const recordTypeOptionsPairs = recordTypeOptions
		.map(record => {
			const recordTypeOption = recordTypeOptionsPairDefinitions.find(
				option => option.value === record,
			);
			return recordTypeOption;
		})
		.filter((option): option is ChoiceListPairs => option !== undefined);

	return {
		enabledRecordTypes: recordTypeOptions,
		recordTypeOptionsFlagged: recordTypeOptionsPairs,
	};
}
